import { ExternalLink } from 'lucide-react';

import { storefrontBrandUrl } from '../lib/storefront-url';
import type { CatalogueCountry } from '../types/brand.types';

/**
 * Customer preview: the brand's live page on each country's storefront, as a
 * customer sees it now. An inactive brand, or one with nothing visible in that
 * country, shows the storefront's "not found" there. Countries without a site
 * address (Settings & readiness) have no link.
 */
export function StorefrontPreviewLinks({
  brandName,
  isActive,
  countries,
}: {
  brandName: string;
  isActive: boolean;
  countries: readonly CatalogueCountry[];
}) {
  const withSite = countries.filter((country) => country.siteUrl);
  if (withSite.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        No storefront address is set for your countries (Countries › Settings), so there is no
        customer preview.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span className="text-muted-foreground">Preview as a customer:</span>
      {withSite.map((country) => (
        <a
          key={country.id}
          href={storefrontBrandUrl(country.siteUrl as string, brandName)}
          target="_blank"
          rel="noreferrer noopener"
          className="text-primary inline-flex items-center gap-1 hover:underline"
          aria-label={`Preview ${brandName} on the ${country.nameEn ?? country.code} storefront (opens a new tab)`}
        >
          {country.nameEn ?? country.code}
          <ExternalLink className="size-3" aria-hidden />
        </a>
      ))}
      {!isActive && (
        <span className="text-muted-foreground">
          (inactive: customers see the storefront's “not found”)
        </span>
      )}
    </div>
  );
}
